import { ApolloCache, DefaultContext, MutationUpdaterFunction } from '@apollo/client';
import {
  colors,
  ControlledAutocompleteInput,
  ControlledInput,
  ControlledTextArea,
  InputFormContainer,
  Stack,
  Typography,
} from '@ui-components/neo.tax';
import { ReactElement, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useRdContext } from '@neo.tax/rd/context';
import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import {
  useCreateProduct,
  useProductOptions,
  Exact,
  GetRdProjectsQuery,
  GetRdProjectsQueryVariables,
  GET_RD_PROJECTS,
  noopFormSubmit,
  ToastConfigState,
  UpdateProductMutation,
  DeleteProductModal,
  UpdateProductModal,
} from '@neo.tax/shared';
import { OrganizationMember } from '@neo.tax/shared/hooks/useCurrentUser';

import { useRdProjectDetailAnalyticIds } from './hooks';
import { RdProjectInfoForm } from './rd-project-detail-form.types';
import { makeDefaultFormValues } from './rd-project-detail-form.utils';

interface RdProjectDetailFormProps {
  rdProject: RdProject;
  members: OrganizationMember[];
}

const UPDATE_RD_PROJECT_REQUEST_KEY = 'UPDATE_RD_PROJECT_REQUEST_KEY';

export const RdProjectDetailForm = ({ members, rdProject }: RdProjectDetailFormProps): ReactElement => {
  const { fiscalYearId } = useRdContext();
  const { clearSelectedProductState, productOptions, productToDelete, productToEdit } = useProductOptions();
  const [createProduct] = useCreateProduct();
  const { clearProductBtnId, clearProjectOwnerBtnId, descriptionId, nameId, productId, projectOwnerId } =
    useRdProjectDetailAnalyticIds();
  const { control, handleSubmit, reset, setValue, watch } = useForm<RdProjectInfoForm>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(rdProject),
  });

  const handleClearProduct = (): void => {
    setValue('productId', null);
  };

  const handleClearProjectOwner = (): void => {
    setValue('projectOwnerId', null);
  };

  const [updateRdProject] = useUpdateRdProject({
    rdProject,
    context: {
      debounceKey: UPDATE_RD_PROJECT_REQUEST_KEY,
      serializationKey: UPDATE_RD_PROJECT_REQUEST_KEY,
    },
  });

  const onDeleteProduct = (cache: ApolloCache<any>): void => {
    const rdProjects = cache.readQuery<GetRdProjectsQuery, GetRdProjectsQueryVariables>({
      query: GET_RD_PROJECTS,
      variables: { fiscalYearId },
    });
    if (rdProjects) {
      const updatedEdges = rdProjects.getRdProjects.edges.map((edge) => ({
        ...edge,
        node: edge.node.product?.id === productToDelete?.id ? { ...edge.node, product: null } : edge.node,
      }));
      cache.writeQuery({
        query: GET_RD_PROJECTS,
        variables: { fiscalYearId },
        data: {
          getRdProjects: {
            ...rdProjects.getRdProjects,
            edges: updatedEdges,
          },
        },
      });
    }
  };

  const onUpdateProduct: MutationUpdaterFunction<
    UpdateProductMutation,
    Exact<{
      productId: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  > = (cache, { data }): void => {
    const rdProjects = cache.readQuery<GetRdProjectsQuery, GetRdProjectsQueryVariables>({
      query: GET_RD_PROJECTS,
      variables: { fiscalYearId },
    });
    if (rdProjects) {
      const updatedEdges = rdProjects.getRdProjects.edges.map((edge) => ({
        ...edge,
        node: edge.node.product?.id === productToEdit?.id ? { ...edge.node, product: data?.updateProduct } : edge.node,
      }));
      cache.writeQuery({
        query: GET_RD_PROJECTS,
        variables: { fiscalYearId },
        data: {
          getRdProjects: {
            ...rdProjects.getRdProjects,
            edges: updatedEdges,
          },
        },
      });
    }
  };

  const onValid: SubmitHandler<RdProjectInfoForm> = useCallback(
    (values): void => {
      updateRdProject({
        name: values.name,
        description: values.description,
        productId: values.productId ?? null,
        projectOwnerId: values.projectOwnerId,
      });
    },
    [updateRdProject],
  );

  const onInvalid = (): void => {
    ToastConfigState.pushErrorToast('Error updating project', {
      description: 'One or more values in the form are invalid. Please correct them to save.',
      duration: 'MEDIUM',
    });
  };
  const onInvalidDebounced = useDebouncedCallback(onInvalid, 500);

  useEffect(() => {
    reset(makeDefaultFormValues(rdProject));

    // ONLY reset the form when the project ID changes otherwise infinite loop after first auto-save
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [rdProject.id, rdProject.processingState, reset, rdProject.product?.id]);

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onValid, onInvalidDebounced)());
    return (): void => subscription.unsubscribe();

    // Watch `project.id` to prevent the watcher from firing when the project changes.
    // Not clear why it's firing when the project changes to begin with.
  }, [handleSubmit, onInvalidDebounced, onValid, watch, rdProject.id]);

  return (
    <>
      <Stack as="form" direction="column" onSubmit={noopFormSubmit}>
        <ControlledInput
          control={control}
          name="name"
          id={nameId}
          label="Project Title"
          placeholder="Improve Website UI"
          rules={{ required: 'Name is required' }}
        />
        <InputFormContainer label="Project Description" inputId={descriptionId}>
          <ControlledTextArea
            control={control}
            name="description"
            id={descriptionId}
            placeholder="Building something amazing!"
            rows={4}
          />
        </InputFormContainer>
        <ControlledAutocompleteInput
          label="Product"
          name="productId"
          options={productOptions}
          optionValueKey="id"
          optionDisplayKey="name"
          searchKeys={['name']}
          placeholder="Type product name"
          id={productId}
          control={control}
          allowCreate
          onCreate={(value): void => {
            createProduct(value, { rdProjectId: rdProject.id });
          }}
          action={
            rdProject.product?.id && (
              <Typography
                as="button"
                fontSize="sm"
                color={colors.primary[600]}
                onClick={handleClearProduct}
                id={clearProductBtnId}
              >
                Clear
              </Typography>
            )
          }
        />
        <ControlledAutocompleteInput
          control={control}
          id={projectOwnerId}
          label="Project Owner"
          name="projectOwnerId"
          options={members}
          optionValueKey="id"
          optionDisplayKey="fullName"
          searchKeys={['fullName']}
          placeholder="Select owner"
          allowCreate={false}
          action={
            rdProject.projectOwner?.id && (
              <Typography
                as="button"
                fontSize="sm"
                color={colors.primary[600]}
                id={clearProjectOwnerBtnId}
                onClick={handleClearProjectOwner}
              >
                Clear
              </Typography>
            )
          }
        />
      </Stack>
      <DeleteProductModal product={productToDelete} onClose={clearSelectedProductState} onDelete={onDeleteProduct} />
      <UpdateProductModal product={productToEdit} onClose={clearSelectedProductState} onUpdate={onUpdateProduct} />
    </>
  );
};
